import type { BoardNodeType, BoardTextStyleId } from '../../types';

/** Sticky-note color palette (background colors). */
export const NOTE_COLORS = [
  '#FFE8A3', // amber
  '#FFD0D0', // rose
  '#C9F0D1', // mint
  '#C9E2FF', // sky
  '#E4D4FF', // lilac
  '#FFFFFF', // white
];

export const DEFAULT_NOTE_COLOR = NOTE_COLORS[0];

/** Default on-canvas pixel size for newly created nodes, by type. */
export const DEFAULT_NODE_SIZE: Record<BoardNodeType, { width: number; height: number }> = {
  image: { width: 280, height: 200 },
  link: { width: 280, height: 230 },
  note: { width: 200, height: 200 },
  text: { width: 240, height: 80 },
  group: { width: 400, height: 300 },
  timeline: { width: 360, height: 160 },
  floorplan: { width: 420, height: 320 },
};

export const BOARD_NOTE_FONT_SIZES = [12, 14, 16, 18, 22] as const;

export const DEFAULT_BOARD_NOTE_FONT_SIZE = 14;

/** Typography presets for free-standing text nodes. */
export interface BoardTextStylePreset {
  id: BoardTextStyleId;
  label: string;
  fontSize: number;
  fontWeight: number;
  lineHeight: number;
  letterSpacing?: string;
  textTransform?: 'uppercase';
  color?: string;
}

export const BOARD_TEXT_STYLES: BoardTextStylePreset[] = [
  { id: 'header', label: 'Header', fontSize: 34, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em' },
  { id: 'title', label: 'Title', fontSize: 26, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' },
  { id: 'subtitle', label: 'Subtitle', fontSize: 19, fontWeight: 600, lineHeight: 1.3, color: '#475569' },
  { id: 'body', label: 'Body', fontSize: 15, fontWeight: 400, lineHeight: 1.5 },
  {
    id: 'caption',
    label: 'Caption',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#64748b',
  },
];

export const DEFAULT_BOARD_TEXT_STYLE: BoardTextStyleId = 'title';

/** Size choices for text nodes (wider range than notes to cover headers). */
export const BOARD_TEXT_FONT_SIZES = [12, 14, 16, 18, 22, 26, 32, 40, 48] as const;

export const BOARD_TIMELINE_MAX_EVENTS = 10;

export const GROUP_PADDING = 24;

export const GROUP_LABEL_HEIGHT = 28;

/** Longest edge (px) for a freshly added image node, before user resizing. */
export const IMAGE_MAX_INITIAL_EDGE = 360;

/** Approximate caption strip height used when hit-testing image media. */
export const IMAGE_CAPTION_HEIGHT = 38;

export const DRAW_COLORS = [
  '#1f2937',
  '#ef4444',
  '#f59e0b',
  '#22c55e',
  '#0ea5e9',
  '#6366f1',
  '#ffffff',
];

export const DRAW_WIDTHS = [2, 4, 8] as const;

export const HIGHLIGHTER_WIDTHS = [12, 20, 32] as const;

export const HIGHLIGHTER_OPACITY = 0.35;

/** Hairline used for callout leaders (engineering-style, not the pen width). */
export const CALLOUT_STROKE = 1.15;
export const CALLOUT_ARROW = 7;

/** Paper fill so a frame hides the canvas behind it without covering nodes. */
export const FRAME_FILL = 'rgba(255, 255, 255, 0.92)';
